import React, { useEffect, useState, useRef, useMemo } from 'react'
import { blueColour, goldColour } from '../pages/Home'
import { motion } from "framer-motion";
import Particles, { initParticlesEngine } from "@tsparticles/react";
import { loadSlim } from "@tsparticles/slim";
import { useStateContext } from '../context/StateContext';
import { FaExpand, FaCompress } from "react-icons/fa";
import T from './Translation';
import useMediaQuery from '../hooks/useMediaQuery';

const MusicPlayerScreen = ({currentLyrics, songs, currentLyric, isPlaying, hideScreen, title}) => {
    const {hideTestButtons} = useStateContext()
    const lyricsToHighlight = !Array.isArray(songs) ? [] : songs?.map((song) => !Array.isArray(song?.lyricsToHighlight) ? [] : song?.lyricsToHighlight?.map((lyric) => lyric?.text)).flat();

    const [isLyricHighlighted, setIsLyricHighlighted] = useState(false)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [wakeLockActive, setWakeLockActive] = useState(false)
    const screenRef = useRef(null)
    const wakeLockRef = useRef(null)
    const isMobile = useMediaQuery("(max-width: 768px)");

    useEffect(() => {
        setIsLyricHighlighted(lyricsToHighlight?.some((lyric) => lyric == currentLyric))
    },[currentLyric])

    // Handle fullscreen change events
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement || 
                           !!document.webkitFullscreenElement || 
                           !!document.mozFullScreenElement ||
                           !!document.msFullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);
        
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, []);

    // Handle wake lock based on isPlaying state
    useEffect(() => {
        const requestWakeLock = async () => {
            if (!('wakeLock' in navigator)) {
                process.env.NODE_ENV === "development" && console.log('Wake Lock API not supported in this browser');
                return;
            }

            // Don't request a new wake lock if we already have one
            if (wakeLockRef.current) {
                process.env.NODE_ENV === "development" && console.log('Wake Lock already active, skipping request');
                return;
            }

            try {
                // Request a wake lock
                wakeLockRef.current = await navigator.wakeLock.request('screen');
                setWakeLockActive(true);
                process.env.NODE_ENV === "development" && console.log('Wake Lock is active');

                // Listen for wake lock release
                const handleRelease = () => {
                    process.env.NODE_ENV === "development" && console.log('Wake Lock was released');
                    setWakeLockActive(false);
                    wakeLockRef.current = null;
                    
                    // If still playing, try to re-acquire the wake lock
                    if (isPlaying) {
                        setTimeout(() => {
                            requestWakeLock();
                        }, 1000);
                    }
                };

                wakeLockRef.current.addEventListener('release', handleRelease);
                
                // Store the release handler for cleanup
                wakeLockRef.current.releaseHandler = handleRelease;
            } catch (err) {
                process.env.NODE_ENV === "development" && console.error(`Failed to request Wake Lock: ${err.message}`);
            }
        };

        const releaseWakeLock = () => {
            if (wakeLockRef.current) {
                // Remove the event listener to prevent re-acquisition
                if (wakeLockRef.current.releaseHandler) {
                    wakeLockRef.current.removeEventListener('release', wakeLockRef.current.releaseHandler);
                }
                
                wakeLockRef.current.release()
                    .then(() => {
                        process.env.NODE_ENV === "development" && console.log('Wake Lock released');
                        setWakeLockActive(false);
                        wakeLockRef.current = null;
                    })
                    .catch((err) => {
                        process.env.NODE_ENV === "development" && console.error(`Error releasing Wake Lock: ${err.message}`);
                    });
            }
        };

        // Request wake lock when playing, release when paused
        if (isPlaying) {
            requestWakeLock();
        } else {
            releaseWakeLock();
        }

        // Clean up on unmount
        return () => {
            if (wakeLockRef.current) {
                // Remove the event listener
                if (wakeLockRef.current.releaseHandler) {
                    wakeLockRef.current.removeEventListener('release', wakeLockRef.current.releaseHandler);
                }
                
                wakeLockRef.current.release().catch(err => {
                    process.env.NODE_ENV === "development" && console.error(`Error releasing Wake Lock on cleanup: ${err.message}`);
                });
            }
        };
    }, [isPlaying]); // Only depend on isPlaying, not wakeLockActive

    // Re-request wake lock when document becomes visible again
    useEffect(() => {
        const handleVisibilityChange = async () => {
            if (document.visibilityState === 'visible' && isPlaying && !wakeLockRef.current && 'wakeLock' in navigator) {
                try {
                    // Small delay to ensure the page is fully visible
                    setTimeout(async () => {
                        if (isPlaying && !wakeLockRef.current) {
                            wakeLockRef.current = await navigator.wakeLock.request('screen');
                            
                            // Add the release handler
                            const handleRelease = () => {
                                process.env.NODE_ENV === "development" && console.log('Wake Lock was released');
                                setWakeLockActive(false);
                                wakeLockRef.current = null;
                            };
                            
                            wakeLockRef.current.addEventListener('release', handleRelease);
                            wakeLockRef.current.releaseHandler = handleRelease;
                            
                            setWakeLockActive(true);
                            process.env.NODE_ENV === "development" && console.log('Wake Lock re-acquired after visibility change');
                        }
                    }, 500);
                } catch (err) {
                    process.env.NODE_ENV === "development" && console.error(`Failed to re-acquire Wake Lock: ${err.message}`);
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [isPlaying]); // Only depend on isPlaying

    // Toggle fullscreen function
    const toggleFullscreen = () => {
        if (!isFullscreen) {
            const element = screenRef.current;
            if (!element) return;

            try {
                // Try standard fullscreen API first
                if (element.requestFullscreen) {
                    element.requestFullscreen().catch(err => {
                        process.env.NODE_ENV === "development" && console.error(`Error attempting to enable fullscreen: ${err.message}`);
                    });
                } 
                // Fallbacks for different browsers
                else if (element.webkitRequestFullscreen) {
                    element.webkitRequestFullscreen();
                } 
                else if (element.mozRequestFullScreen) {
                    element.mozRequestFullScreen();
                } 
                else if (element.msRequestFullscreen) {
                    element.msRequestFullscreen();
                }
                // Special handling for iOS Safari
                else if (isMobile) {
                    // For iOS Safari, we'll use CSS to simulate fullscreen
                    element.style.position = 'fixed';
                    element.style.top = '0';
                    element.style.left = '0';
                    element.style.width = '100%';
                    element.style.height = '100%';
                    element.style.zIndex = '9999';
                    setIsFullscreen(true);
                }
            } catch (err) {
                process.env.NODE_ENV === "development" && console.error(`Error attempting to enable fullscreen: ${err.message}`);
            }
        } else {
            try {
                // Standard exit fullscreen
                if (document.exitFullscreen) {
                    document.exitFullscreen();
                } 
                // Fallbacks for different browsers
                else if (document.webkitExitFullscreen) {
                    document.webkitExitFullscreen();
                } 
                else if (document.mozCancelFullScreen) {
                    document.mozCancelFullScreen();
                } 
                else if (document.msExitFullscreen) {
                    document.msExitFullscreen();
                }
                // Special handling for iOS Safari
                else if (isMobile) {
                    // Reset the CSS properties
                    const element = screenRef.current;
                    if (element) {
                        element.style.position = '';
                        element.style.top = '';
                        element.style.left = '';
                        element.style.width = '';
                        element.style.height = '';
                        element.style.zIndex = '';
                    }
                    setIsFullscreen(false);
                }
            } catch (err) {
                process.env.NODE_ENV === "development" && console.error(`Error attempting to exit fullscreen: ${err.message}`);
            }
        }
    };
    const textColour = (currentLyric, isLyricHighlighted) => {
        if (currentLyric && isLyricHighlighted) {
            return { color: goldColour };
        }
        if(!currentLyric){
            return { color: goldColour };}
        return { color: "white" };
    }
    const testFunction = () => {
        process.env.NODE_ENV === "development" && console.log("testFunction", {songs, lyricsToHighlight, currentLyric, isLyricHighlighted})
    }
    if(hideScreen) return null
    return (
        <div 
            ref={screenRef}
            style={{
                marginBottom:"50px", 
                width:"100%", 
                height: isFullscreen ? "100vh" : "500px", 
                backgroundColor:blueColour, 
                display:"flex", 
                flexDirection: "column",
                justifyContent:"center", 
                alignItems:"center", 
                color:"white",
                position: isFullscreen && isMobile ? "fixed" : "relative",
                top: isFullscreen && isMobile ? "0" : "auto",
                left: isFullscreen && isMobile ? "0" : "auto",
                zIndex: isFullscreen && isMobile ? "9999" : "auto"
            }}
        >
            {/* Control buttons */}
            <div style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                zIndex: 10,
                display: "flex",
                gap: "10px",
                alignItems: "center"
            }}>
                {/* Wake lock indicator */}
                {wakeLockActive && (
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: "rgba(0,0,0,0.3)",
                        padding: "5px 10px",
                        borderRadius: "15px",
                        fontSize: "12px"
                    }}>
                        <span style={{ color: goldColour, marginRight: "5px" }}>●</span>
                        <span><T obj={{en: "Screen will stay on", it:"Schermo rimane acceso"}}/></span>
                    </div>
                )}
                
                {/* Fullscreen button */}
                <button 
                    onClick={(e) => {
                        e.stopPropagation(); // Prevent testFunction from firing
                        toggleFullscreen();
                    }}
                    onTouchEnd={(e) => {
                        e.stopPropagation(); // Prevent testFunction from firing
                        toggleFullscreen();
                    }}
                    style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: isMobile ? '15px' : '10px', // Larger touch target on mobile
                        touchAction: 'manipulation' // Optimize for touch
                    }}
                >
                    {isFullscreen ? 
                        <FaCompress size={isMobile ? 40 : 34} color={goldColour} /> : 
                        <FaExpand size={isMobile ? 40 : 34} color={goldColour} />
                    }
                </button>
            </div>
            
            <Background 
                active={!!currentLyric} 
                isPlaying={isPlaying} 
                style={{
                    width:"100%", 
                    height:"100%", 
                    display:"flex", 
                    justifyContent:"center", 
                    alignItems:"center"
                }}
            >
                {currentLyrics && (
                    <div 
                        className='bebas' 
                        style={{
                            textAlign: 'center',
                            margin: '20px 0',
                            padding: '15px',
                            borderRadius: '5px',
                            minHeight: '50px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                        onClick={testFunction}
                    >
                        <p style={{
                            ...textColour(currentLyric, isLyricHighlighted),
                            fontSize: isMobile 
                                ? (isFullscreen ? '2.5em' : '1.5em')  // Smaller font on mobile
                                : (isFullscreen ? '6em' : '4em'),     // Original size on desktop
                            margin: 0,
                            wordWrap: 'break-word',                   // Ensure text wraps properly
                            maxWidth: '100%'                          // Prevent overflow
                        }}>
                            {currentLyric || title}
                        </p>
                    </div>
                )}
            </Background>
        </div>
    )
}

export default MusicPlayerScreen


export const Background = ({ style = {}, children, active, highlighted, isPlaying }) => {
    const [init, setInit] = useState(false);
     const {hideTestButtons} = useStateContext()
    const containerRef = useRef(null);
    const engineInitializedRef = useRef(false);

    // Only initialize particles engine when playing and active
    useEffect(() => {
        // Only initialize if playing and active
        if (isPlaying && !init && !engineInitializedRef.current) {
            process.env.NODE_ENV === "development" && console.log("Initializing particles engine");
            engineInitializedRef.current = true;
            
            initParticlesEngine(async (engine) => {
                await loadSlim(engine);
            }).then(() => {
                if (isPlaying) { // Double-check conditions before setting init
                    setInit(true);
                    process.env.NODE_ENV === "development" && console.log("Particles initialized");
                }
            });
        } 
        // Completely destroy particles when not playing
        else if ((!isPlaying) && init) {
            process.env.NODE_ENV === "development" && console.log("Destroying particles");
            setInit(false);
            // Reset engineInitializedRef so particles can be reinitialized when playing resumes
            engineInitializedRef.current = false;
            process.env.NODE_ENV === "development" && console.log("Reset particles engine reference");
        }
    }, [init, isPlaying]);
    
    const particlesOpacity = 0.1;
    // Memoized particles options with reduced particle count
    const options = useMemo(() => ({
        fpsLimit: 24, // Lower FPS for better performance
        particles: {
            number: {
                value: 800, // Reduced from 1700 to improve performance
                density: { enable: true, area: 800 }
            },
            color: { value: ["#ffffff", "#ffd700"] },
            shape: { type: "circle" },
            opacity: {
                value: particlesOpacity,
                random: true,
                animation: { enable: true, speed: 0.1, minimumValue: 0.3, sync: false }
            },
            size: {
                value: 54,
                random: true,
                animation: { enable: true, speed: 0.2, minimumValue: 0.5, sync: false }
            },
            move: {
                enable: true,
                speed: 0.05, // Reduced speed for smoother motion
                direction: "none",
                outModes: { default: "bounce" }
            }
        },
        background: { color: "transparent" },
        detectRetina: true,
        smooth: true
    }), []);

    // Memoized Particles component - only render when initialized and playing
    const ParticlesComponent = useMemo(() => (
        init && isPlaying && active ? (
            <Particles
                id="tsparticles"
                options={options}
                style={{ width: "100%", height: "100%" }}
            />
        ) : null
    ), [init, options, isPlaying, active]);

    return (
        <>
            {active ? (
                <div style={{ ...style, position: "relative", overflow: "hidden" }}>
                    {/* Animated Background - only animate when playing */}
                    <motion.div
                        animate={isPlaying ? {
                            backgroundColor: [
                                `rgba(32, 35, 215, 0.5)`,
                                `rgba(5, 148, 5, 0.5)`,
                                `rgba(3, 3, 85, 0.5)`
                            ]
                        } : {}}
                        transition={isPlaying ? {
                            duration: 25,
                            repeat: Infinity,
                            repeatType: "reverse",
                            ease: "linear"
                        } : {}}
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 0,
                            backgroundColor: `rgba(255, 0, 0, ${isPlaying ? 0.5 : 0.2})`
                        }}
                    />

                    {/* Particle Effect */}
                    <div ref={containerRef} style={{
                        position: "absolute",
                        top: 0, left: 0,
                        width: "100%", height: "100%",
                        pointerEvents: "none",
                        zIndex: 3,
                        mixBlendMode: "lighten"
                    }}>
                        {ParticlesComponent}
                    </div>

                    {/* Content */}
                    <div style={{ position: "relative", zIndex: 2, pointerEvents: "auto" }}>
                        {children}
                    </div>
                </div>
            ) : (
                <div style={style}>{children}</div>
            )}
        </>
    );
};
